// ============================================
// TopNavBar Component
// Top navigation bar with search, notifications, and user menu
// ============================================

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES } from '@conai/shared-constants';
import { cn } from '../../utils/cn';
import {
  BellIcon,
  ChevronDownIcon,
  MenuIcon,
} from '../icons';
import { useAuthStore } from '../../stores/auth.store';
import { useStoreSelectionStore } from '../../stores/store-selection.store';
import { SelectDropdown } from '../shared/SelectDropdown';
import { useI18n } from '../../i18n';
import { GlobalSearch } from './GlobalSearch';
import {
  useNotifications,
  useMarkNotificationAsRead,
  useDeleteNotification,
} from '../../hooks';
import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';
import { resolveNotificationRoute } from '../../utils/notificationRoute';
import { env } from '../../config/env';

// ============================================
// Types
// ============================================

interface TopNavBarProps {
  onMenuClick: () => void;
}

// ============================================
// Component
// ============================================

export function TopNavBar({ onMenuClick }: TopNavBarProps) {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isAllNotificationsOpen, setIsAllNotificationsOpen] = useState(false);
  /** Bug #140: thông báo đang mở chi tiết (mọi loại, không riêng promotion). */
  const [selectedNotification, setSelectedNotification] = useState<any>(null);
  /** Bug #133: popup Help & Support — app chưa có route riêng cho trang này. */
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);

  // Bug #129: lock background scroll while the "All Notifications" modal is open
  useLockBodyScroll(isAllNotificationsOpen || !!selectedNotification || isHelpOpen);

  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const { selectedStoreId, availableStores, setSelectedStoreById } = useStoreSelectionStore();
  const { t, language, setLanguage } = useI18n();
  const navigate = useNavigate();

  // Load Real API notifications using query hook
  const { data: notificationsData, isLoading: isNotiLoading } = useNotifications({ page_size: 20 });
  const markAsRead = useMarkNotificationAsRead();
  const deleteNoti = useDeleteNotification();

  const notifications = notificationsData?.items ?? [];
  /** Tổng số thật từ `meta.count` — endpoint chỉ trả tối đa 100 dòng mới nhất. */
  const totalNotifications = notificationsData?.count ?? notifications.length;
  const unreadNotifications = notifications.filter((n) => !n.is_read);
  const hasUnread = unreadNotifications.length > 0;

  const getIconByType = (type: string) => {
    switch (type?.toUpperCase()) {
      case 'ORDER':
      case 'SALES':
        return { emoji: '🛒', bg: 'bg-blue-100 text-blue-600' };
      case 'STOCK':
      case 'LOW_STOCK':
        return { emoji: '⚠️', bg: 'bg-orange-100 text-orange-600' };
      case 'SYSTEM':
        return { emoji: '✅', bg: 'bg-green-100 text-green-600' };
      default:
        return { emoji: '📣', bg: 'bg-purple-100 text-purple-600' };
    }
  };

  // Helper to format translation keys or raw notifications text beautifully
  const formatNotiText = (text: string) => {
    if (!text) return '';
    if (text.startsWith('notification.')) {
      const clean = text
        .replace('notification.', '')
        .replace('.title', '')
        .replace('.body', '')
        .replace(/_/g, ' ');
      return clean.charAt(0).toUpperCase() + clean.slice(1);
    }
    return text;
  };

  const handleNotificationClick = (noti: any) => {
    // Bấm vào thông báo LUÔN đánh dấu đã đọc, kể cả khi không đi đâu được.
    // Không `await` trước khi điều hướng: mạng chậm thì màn hình đứng im vài
    // giây, đúng cái cảm giác "bấm không ăn" mà bug #161 mô tả.
    if (!noti.is_read) {
      markAsRead.mutate(noti.id);
    }

    // Bug #161: đích đến lấy từ `reference_type`/`reference_id` do BE trả về,
    // không đoán ở FE nữa. Xem `utils/notificationRoute.ts`.
    const route = resolveNotificationRoute(noti);

    if (route) {
      setIsNotificationOpen(false);
      setIsAllNotificationsOpen(false);
      setSelectedNotification(null);
      navigate(route);
      return;
    }

    // Không xác định được màn tương ứng (BE không suy được đích đến, hoặc
    // store-admin chưa có màn cho loại đó) -> mở chi tiết ngay tại chỗ. Vẫn là
    // một phản hồi thấy được, không im lặng.
    setSelectedNotification(noti);
    setIsNotificationOpen(false);
  };

  const handleDeleteNoti = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    await deleteNoti.mutateAsync(id);
  };

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setIsNotificationOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
  };

  const handleProfileSettings = () => {
    navigate(ADMIN_ROUTES.SETTINGS);
    setIsUserMenuOpen(false);
  };

  // Bug #133: nút này từng navigate sang đúng trang Settings như "Profile
  // Settings" — bấm vào không thấy gì đổi nên bị báo là "không click được".
  // Chưa có trang Help/Support trong router, nên mở popup hỗ trợ tại chỗ.
  const handleHelpSupport = () => {
    setIsHelpOpen(true);
    setIsUserMenuOpen(false);
  };

  const handleViewAllNotifications = () => {
    setIsAllNotificationsOpen(true);
    setIsNotificationOpen(false);
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.full_name) return 'U';
    const parts = user.full_name.split(' ');
    const first = parts[0]?.[0] || '';
    const last = parts[parts.length - 1]?.[0] || '';
    return `${first}${last}`.toUpperCase() || 'U';
  };

  return (
    <header className="fixed left-0 right-0 top-0 z-30 h-16 bg-white/95 backdrop-blur-sm lg:left-64">
      <div className="flex h-full items-center justify-between border-b border-slate-100 px-4 lg:px-6">
        {/* Left section */}
        <div className="flex items-center gap-4">
          {/* Mobile menu button */}
          <button
            onClick={onMenuClick}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700 lg:hidden"
            aria-label="Open menu"
          >
            <MenuIcon size={20} />
          </button>

          {/* Search */}
          <div className="hidden sm:block">
            <GlobalSearch />
          </div>
        </div>

        {/* Right section */}
        <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
          {/* Droplist tuỳ biến — không dùng <select> native để mobile không mở popup hệ điều hành */}
          <SelectDropdown
            size="sm"
            value={language}
            onChange={(next) => {
              if (next === 'pl' || next === 'en' || next === 'uk') {
                setLanguage(next);
              }
            }}
            options={[
              { value: 'en', label: t('languageShortEnglish') },
              { value: 'pl', label: t('languageShortPolish') },
              { value: 'uk', label: t('languageShortUkrainian') },
            ]}
            className="w-[64px] shrink-0"
          />

          {/* Store selector */}
          <SelectDropdown
            size="sm"
            value={selectedStoreId ?? ''}
            onChange={(v) => setSelectedStoreById(v || null)}
            options={[
              { value: '', label: t('navSelectStore') },
              ...availableStores.map((store) => ({ value: store.id, label: store.name })),
            ]}
            className="min-w-0 max-w-[130px] sm:max-w-none sm:w-[180px] md:mr-2"
          />

          {/* Notifications */}
          <div className="relative" ref={notificationRef}>
            <button
              onClick={() => setIsNotificationOpen(!isNotificationOpen)}
              className="relative rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
              aria-label="Notifications"
            >
              <BellIcon size={20} />
              {/* Notification badge */}
              {hasUnread && (
                <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-red-500" />
              )}
            </button>

            {/* Notification dropdown — mobile: full ngang màn hình; desktop: neo theo chuông */}
            {isNotificationOpen && (
              <div className="fixed inset-x-3 top-16 z-50 rounded-xl border border-slate-100 bg-white p-4 shadow-lg sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-sm font-semibold text-slate-900">
                    {t('navNotifications')}
                  </h3>
                  {hasUnread && (
                    <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                      {unreadNotifications.length} new
                    </span>
                  )}
                </div>
                
                {isNotiLoading ? (
                  <div className="flex justify-center py-4">
                    <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-primary-600" />
                  </div>
                ) : notifications.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-6">No notifications</p>
                ) : (
                  /* `overscroll-contain` — cùng lý do với modal bên dưới (bug #252). */
                  <div className="max-h-60 space-y-2 overflow-y-auto overscroll-contain">
                    {notifications.slice(0, 5).map((noti) => {
                      const icon = getIconByType(noti.type);
                      return (
                        <div
                          key={noti.id}
                          onClick={() => handleNotificationClick(noti)}
                          className={cn(
                            "flex gap-3 rounded-lg p-2.5 transition-colors cursor-pointer relative group",
                            noti.is_read ? "hover:bg-slate-50" : "bg-blue-50/50 hover:bg-blue-50"
                          )}
                        >
                          <div className={cn("h-8 w-8 shrink-0 rounded-full flex items-center justify-center text-base", icon.bg)}>
                            {icon.emoji}
                          </div>
                          <div className="flex-1 min-w-0 pr-6">
                            <p className="text-sm font-semibold text-slate-800 truncate">{formatNotiText(noti.title)}</p>
                            <p className="text-xs text-slate-600 truncate mt-0.5">{formatNotiText(noti.body)}</p>
                            <p className="text-[10px] text-slate-400 mt-1">{new Date(noti.created_at).toLocaleString()}</p>
                          </div>
                          
                          {/* Close / Delete Action on Hover */}
                          <button
                            onClick={(e) => handleDeleteNoti(e, noti.id)}
                            className="absolute right-2 top-2 rounded p-0.5 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Delete"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M18 6 6 18M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                <button
                  onClick={handleViewAllNotifications}
                  className="mt-3 w-full text-center text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  {t('navViewAllNotifications')}
                </button>
              </div>
            )}
          </div>

          {/* User menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-2 rounded-lg p-1.5 transition-colors hover:bg-slate-100"
            >
              {/* Avatar */}
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-sm font-medium text-white">
                {getUserInitials()}
              </div>
              <div className="hidden text-left md:block">
                <p className="text-sm font-medium text-slate-700">
                  {user?.full_name}
                </p>
                <p className="text-xs text-slate-500">{user?.role || 'Admin'}</p>
              </div>
              <ChevronDownIcon
                size={16}
                className={cn(
                  'hidden text-slate-400 transition-transform md:block',
                  isUserMenuOpen && 'rotate-180'
                )}
              />
            </button>

            {/* User dropdown */}
            {isUserMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 rounded-xl border border-slate-100 bg-white py-2 shadow-lg">
                <div className="border-b border-slate-100 px-4 pb-3 pt-1">
                  <p className="text-sm font-medium text-slate-900">
                    {user?.full_name}
                  </p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <div className="py-1">
                  <button
                    onClick={handleProfileSettings}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    {t('navProfileSettings')}
                  </button>
                  <button
                    onClick={handleHelpSupport}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    {t('navHelpSupport')}
                  </button>
                </div>
                <div className="border-t border-slate-100 py-1">
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    {t('navLogout')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* All Notifications Modal */}
      {isAllNotificationsOpen && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          onClick={() => setIsAllNotificationsOpen(false)}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-4">
              <h3 className="text-lg font-semibold text-slate-900">
                {t('navNotifications')}
                {/* `meta.count` = tổng số thật; danh sách bên dưới bị BE cắt ở
                    100 dòng mới nhất nên hai con số có thể lệch nhau. */}
                {totalNotifications > 0 && (
                  <span className="ml-2 text-sm font-medium text-slate-400">
                    {totalNotifications}
                  </span>
                )}
              </h3>
              <button
                onClick={() => setIsAllNotificationsOpen(false)}
                className="rounded p-1 text-slate-400 hover:text-slate-600"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            {/* `overscroll-contain` (bug #252) — scroll chaining.
                Cuộn danh sách thông báo tới đáy rồi lăn tiếp thì trình duyệt
                chuyển tiếp cú lăn sang vùng cuộn phía sau: màn nền trôi đi
                trong khi popup vẫn mở, và khi lăn ngược lên thì cú lăn tiếp
                tục ăn vào màn nền chứ không quay lại popup — người dùng thấy
                như popup mất luôn khả năng cuộn lên.
                `useLockBodyScroll` ở trên KHÔNG đỡ được ca này: nó khoá
                html/body, còn thứ đang cuộn phía sau là container của
                MainLayout. `overscroll-behavior: contain` chặn ngay tại vùng
                cuộn này, không phụ thuộc vào cái gì nằm sau. */}
            <div className="max-h-[60vh] space-y-3 overflow-y-auto overscroll-contain border-t border-slate-100 px-6 py-5">
              {notifications.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">No notifications</p>
              ) : (
                notifications.map((noti) => {
                  const icon = getIconByType(noti.type);
                  return (
                    <div
                      key={noti.id}
                      onClick={() => handleNotificationClick(noti)}
                      className={cn(
                        "flex gap-4 rounded-xl p-4 border transition-colors cursor-pointer relative group",
                        noti.is_read ? "bg-slate-50/50 border-slate-100 hover:bg-slate-50" : "bg-blue-50/30 border-blue-100/50 hover:bg-blue-50/50"
                      )}
                    >
                      <div className={cn("h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-lg", icon.bg)}>
                        {icon.emoji}
                      </div>
                      <div className="flex-1 min-w-0 pr-6">
                        <p className="text-sm font-semibold text-slate-800">{formatNotiText(noti.title)}</p>
                        <p className="text-xs text-slate-600 mt-1">{formatNotiText(noti.body)}</p>
                        <p className="text-[10px] text-slate-400 mt-2">{new Date(noti.created_at).toLocaleString()}</p>
                      </div>

                      {/* Delete Action on Hover */}
                      <button
                        onClick={(e) => handleDeleteNoti(e, noti.id)}
                        className="absolute right-3 top-3 rounded p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Delete"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6 6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-slate-100 px-6 py-4 flex justify-end">
              <button
                onClick={() => setIsAllNotificationsOpen(false)}
                className="rounded-lg bg-slate-100 px-5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Bug #140: chi tiết 1 thông báo — dùng được cho MỌI loại (Basket updated,
          Payment successful, ...), không chỉ thông báo khuyến mãi. */}
      {selectedNotification && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          onClick={() => setSelectedNotification(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between px-6 pt-6 pb-4">
              <div className="flex items-start gap-3">
                <div
                  className={cn(
                    'h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-lg',
                    getIconByType(selectedNotification.type).bg
                  )}
                >
                  {getIconByType(selectedNotification.type).emoji}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-900">
                    {formatNotiText(selectedNotification.title)}
                  </h3>
                  <p className="text-xs text-slate-400">
                    {new Date(selectedNotification.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedNotification(null)}
                className="rounded p-1 text-slate-400 hover:text-slate-600"
                aria-label={t('close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="border-t border-slate-100 px-6 py-5">
              <p className="whitespace-pre-line text-sm leading-relaxed text-slate-700">
                {formatNotiText(selectedNotification.body) || t('noResults')}
              </p>
            </div>

            <div className="border-t border-slate-100 px-6 py-4 flex justify-end">
              <button
                onClick={() => setSelectedNotification(null)}
                className="rounded-lg bg-slate-100 px-5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Bug #133: popup Help & Support */}
      {isHelpOpen && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
          onClick={() => setIsHelpOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 pt-6 pb-4">
              <h3 className="text-lg font-semibold text-slate-900">{t('navHelpSupport')}</h3>
              <button
                onClick={() => setIsHelpOpen(false)}
                className="rounded p-1 text-slate-400 hover:text-slate-600"
                aria-label={t('close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="border-t border-slate-100 px-6 py-5 space-y-4 text-sm">
              <p className="text-slate-600">{t('helpSupportIntro')}</p>

              <div className="rounded-xl bg-slate-50 p-4 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-slate-500">{t('helpSupportEmail')}</span>
                  <a
                    href="mailto:support@conai.store"
                    className="font-semibold text-primary-600 hover:text-primary-700"
                  >
                    support@conai.store
                  </a>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-slate-500">{t('helpSupportHotline')}</span>
                  <a href="tel:+842873000000" className="font-semibold text-slate-800">
                    +84 28 7300 0000
                  </a>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-slate-500">{t('helpSupportHours')}</span>
                  <span className="font-semibold text-slate-800">08:00 – 18:00</span>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-3 text-xs text-slate-400">
                <span>{t('helpSupportAppVersion')}</span>
                <span className="font-mono">{env.appName} v{env.appVersion}</span>
              </div>
            </div>

            <div className="border-t border-slate-100 px-6 py-4 flex justify-end">
              <button
                onClick={() => setIsHelpOpen(false)}
                className="rounded-lg bg-slate-100 px-5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
}
